import { gsap } from 'gsap'

const PIXELS_PER_SECOND = 100
const DEFAULT_SPEED = 1.1
const RESIZE_DEBOUNCE_MS = 150

const reducedMotionQuery = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)')

export default ({
  speed = DEFAULT_SPEED,
  pauseOnHover = true,
  hoverTimeScale = 0.25,
} = {}) => ({
  speed,
  pauseOnHover,
  hoverTimeScale,
  loop: null,
  items: [],
  resizeObserver: null,
  resizeTimer: null,
  motionQuery: null,
  cleanups: [],

  init() {
    this.motionQuery = reducedMotionQuery()
    const onMotionChange = () => {
      if (this.motionQuery.matches) {
        this.teardownLoop()
      } else if (!this.loop) {
        this.start()
      }
    }
    this.motionQuery.addEventListener('change', onMotionChange)
    this.cleanups.push(() =>
      this.motionQuery.removeEventListener('change', onMotionChange),
    )

    if (!this.motionQuery.matches) {
      this.start()
    }
  },

  destroy() {
    this.teardownLoop()
    this.cleanups.forEach(fn => fn())
    this.cleanups = []
  },

  start() {
    const root = this.$refs.gsapMarquee
    const items = Array.from(root.querySelectorAll('.js-item'))
    if (!items.length) return

    this.items = items
    this.build(items)

    if (this.pauseOnHover) {
      const onEnter = () => this.loop?.timeScale(this.hoverTimeScale)
      const onLeave = () => this.loop?.timeScale(1)
      root.addEventListener('mouseenter', onEnter)
      root.addEventListener('mouseleave', onLeave)
      this.cleanups.push(() => {
        root.removeEventListener('mouseenter', onEnter)
        root.removeEventListener('mouseleave', onLeave)
      })
    }

    let lastWidth = root.clientWidth
    this.resizeObserver = new ResizeObserver(() => {
      if (root.clientWidth === lastWidth) return
      lastWidth = root.clientWidth
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => this.rebuild(items), RESIZE_DEBOUNCE_MS)
    })
    this.resizeObserver.observe(root)
  },

  teardownLoop() {
    clearTimeout(this.resizeTimer)
    this.resizeTimer = null
    this.loop?.kill()
    this.loop = null
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    if (this.items.length) {
      gsap.set(this.items, { clearProps: 'x,xPercent' })
    }
  },

  build(items) {
    this.loop = horizontalLoop(items, {
      repeat: -1,
      speed: this.speed,
    })
  },

  rebuild(items) {
    const progress = this.loop?.progress() ?? 0
    this.loop?.kill()
    gsap.set(items, { clearProps: 'x,xPercent' })
    this.build(items)
    this.loop?.progress(progress)
  },
})

// Seamless horizontal loop. Adapted from GSAP's official helper, trimmed to
// the features this project needs (no Draggable/Inertia). Uses xPercent so
// items stay responsive when widths change.
function horizontalLoop(items, config = {}) {
  items = gsap.utils.toArray(items)
  const length = items.length
  const startX = items[0].offsetLeft
  const widths = []
  const xPercents = []
  const pixelsPerSecond = config.speed * PIXELS_PER_SECOND
  const snap = config.snap === false ? v => v : gsap.utils.snap(config.snap || 1)

  const tl = gsap.timeline({
    repeat: config.repeat ?? -1,
    paused: config.paused,
    defaults: { ease: 'none' },
  })

  items.forEach((el, i) => {
    widths[i] = parseFloat(gsap.getProperty(el, 'width', 'px'))
    xPercents[i] = snap(
      (parseFloat(gsap.getProperty(el, 'x', 'px')) / widths[i]) * 100 +
        gsap.getProperty(el, 'xPercent'),
    )
  })

  gsap.set(items, { xPercent: i => xPercents[i], x: 0 })

  const last = items[length - 1]
  const totalWidth =
    last.offsetLeft +
    (xPercents[length - 1] / 100) * widths[length - 1] -
    startX +
    last.offsetWidth * gsap.getProperty(last, 'scaleX') +
    (parseFloat(config.paddingRight) || 0)

  for (let i = 0; i < length; i++) {
    const item = items[i]
    const curX = (xPercents[i] / 100) * widths[i]
    const distanceToStart = item.offsetLeft + curX - startX
    const distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, 'scaleX')

    tl.to(
      item,
      {
        xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
        duration: distanceToLoop / pixelsPerSecond,
      },
      0,
    ).fromTo(
      item,
      { xPercent: snap(((curX - distanceToLoop + totalWidth) / widths[i]) * 100) },
      {
        xPercent: xPercents[i],
        duration: (totalWidth - distanceToLoop) / pixelsPerSecond,
        immediateRender: false,
      },
      distanceToLoop / pixelsPerSecond,
    )
  }

  tl.progress(1, true).progress(0, true) // pre-render for performance

  return tl
}
